import React from 'react';
import { motion } from 'framer-motion';
import { FileText, CheckCircle, AlertCircle, Scale, UserX, Shield } from 'lucide-react';
export function TermsOfServicePage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1734">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1735">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1736">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1737">
            <span className="w-8 h-px bg-teal" data-id="element-1738"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1739">
              Legal
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1740"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1741">
            Terms of Service
          </h1>

          <p className="text-lg text-ink-muted" data-id="element-1742">
            Last Updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1743">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1744">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1745">
            These Terms of Service ("Terms") govern your access to and use of
            SyNeural's platform, services, and website (collectively, the
            "Services"). By accessing or using our Services, you agree to be
            bound by these Terms.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1746">
            If you do not agree to these Terms, you may not access or use our
            Services. If you are using our Services on behalf of an
            organization, you represent that you have the authority to bind that
            organization to these Terms.
          </p>
        </motion.div>
      </section>

      {/* Content Sections */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto space-y-16" data-id="element-1747">
        {/* Acceptance of Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1748">
          <div className="flex items-center gap-4 mb-6" data-id="element-1749">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1750">
              <CheckCircle size={24} data-id="element-1751" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1752">
              Acceptance of Terms
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed" data-id="element-1753">
            By creating an account, accessing our platform, or using any of our
            Services, you acknowledge that you have read, understood, and agree
            to be bound by these Terms, as well as our Privacy Policy. These
            Terms constitute a legally binding agreement between you and
            SyNeural.
          </p>
        </motion.div>

        {/* Eligibility */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1754">
          <div className="flex items-center gap-4 mb-6" data-id="element-1755">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1756">
              <UserX size={24} data-id="element-1757" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1758">
              Eligibility
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1759">
            To use our Services, you must:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1760">
            <li data-id="element-1761">
              Be at least 13 years of age (or the age of majority in your
              jurisdiction)
            </li>
            <li data-id="element-1762">Have the legal capacity to enter into a binding agreement</li>
            <li data-id="element-1763">
              Not be prohibited from using our Services under applicable law
            </li>
            <li data-id="element-1764">Provide accurate and complete registration information</li>
          </ul>
          <p className="text-ink-muted leading-relaxed mt-4" data-id="element-1765">
            For users under 18, access through educational institutions or with
            parental consent may be required.
          </p>
        </motion.div>

        {/* Account Registration and Security */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1766">
          <div className="flex items-center gap-4 mb-6" data-id="element-1767">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1768">
              <Shield size={24} data-id="element-1769" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1770">
              Account Registration and Security
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1771">
            <p data-id="element-1772">When you create an account, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1773">
              <li data-id="element-1774">Provide accurate, current, and complete information</li>
              <li data-id="element-1775">Maintain and update your information to keep it accurate</li>
              <li data-id="element-1776">
                Maintain the security and confidentiality of your account
                credentials
              </li>
              <li data-id="element-1777">
                Notify us immediately of any unauthorized access or security
                breach
              </li>
              <li data-id="element-1778">
                Accept responsibility for all activities that occur under your
                account
              </li>
            </ul>
            <p data-id="element-1779">
              You may not share your account credentials, create multiple
              accounts, or allow others to use your account. SyNeural reserves
              the right to suspend or terminate accounts that violate these
              requirements.
            </p>
          </div>
        </motion.div>

        {/* Use of Services */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1780">
          <div className="flex items-center gap-4 mb-6" data-id="element-1781">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1782">
              <FileText size={24} data-id="element-1783" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1784">
              Permitted Use
            </h2>
          </div>

          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1785">
            You may use our Services for lawful purposes in accordance with
            these Terms. You agree not to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-ink-muted ml-4" data-id="element-1786">
            <li data-id="element-1787">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-1788">
              Use the Services for any fraudulent, harmful, or malicious purpose
            </li>
            <li data-id="element-1789">
              Attempt to gain unauthorized access to our systems or other users'
              accounts
            </li>
            <li data-id="element-1790">
              Interfere with or disrupt the integrity or performance of the
              Services
            </li>
            <li data-id="element-1791">
              Reverse engineer, decompile, or attempt to extract source code
            </li>
            <li data-id="element-1792">
              Use automated tools (bots, scrapers) without our written
              permission
            </li>
            <li data-id="element-1793">Misrepresent your identity or affiliation</li>
            <li data-id="element-1794">
              Share, sell, or redistribute assessment content or results without
              authorization
            </li>
            <li data-id="element-1795">Use the Services to harass, abuse, or harm others</li>
          </ul>
        </motion.div>

        {/* Intellectual Property */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1796">
          <div className="flex items-center gap-4 mb-6" data-id="element-1797">
            <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1798">
              <Scale size={24} data-id="element-1799" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1800">
              Intellectual Property Rights
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1801">
            <p data-id="element-1802">
              All content, features, functionality, and materials available
              through our Services, including but not limited to text, graphics,
              logos, assessment frameworks, algorithms, software, and data
              compilations, are owned by SyNeural or its licensors and are
              protected by copyright, trademark, patent, and other intellectual
              property laws.
            </p>
            <p data-id="element-1803">
              We grant you a limited, non-exclusive, non-transferable, revocable
              license to access and use our Services for your personal or
              internal organizational use. This license does not include any
              right to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1804">
              <li data-id="element-1805">
                Reproduce, distribute, or create derivative works from our
                content
              </li>
              <li data-id="element-1806">
                Use our trademarks, logos, or branding without written
                permission
              </li>
              <li data-id="element-1807">
                Remove or alter any copyright, trademark, or proprietary notices
              </li>
              <li data-id="element-1808">Commercially exploit our Services or content</li>
            </ul>
          </div>
        </motion.div>

        {/* User Content and Data */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1809">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1810">
            User Content and Data
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1811">
            <p data-id="element-1812">
              You retain ownership of any content, data, or information you
              provide through our Services ("User Content"). By providing User
              Content, you grant SyNeural a worldwide, non-exclusive,
              royalty-free license to use, process, analyze, and store such
              content to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1813">
              <li data-id="element-1814">Provide and improve our Services</li>
              <li data-id="element-1815">Generate assessment results and recommendations</li>
              <li data-id="element-1816">
                Conduct research and platform development (using anonymized
                data)
              </li>
              <li data-id="element-1817">Comply with legal obligations</li>
            </ul>
            <p data-id="element-1818">
              You represent and warrant that you have all necessary rights to
              provide User Content and that such content does not violate any
              third-party rights or applicable laws.
            </p>
          </div>
        </motion.div>

        {/* Payment and Subscriptions */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1819">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1820">
            Payment and Subscriptions
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1821">
            <p data-id="element-1822">
              Certain features of our Services may require payment. If you
              purchase a subscription or paid service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1823">
              <li data-id="element-1824">
                You agree to pay all applicable fees as described at the time of
                purchase
              </li>
              <li data-id="element-1825">
                Fees are non-refundable except as required by law or as
                specified in our refund policy
              </li>
              <li data-id="element-1826">
                Subscriptions automatically renew unless canceled before the
                renewal date
              </li>
              <li data-id="element-1827">
                We reserve the right to change pricing with advance notice
              </li>
              <li data-id="element-1828">
                You are responsible for all taxes associated with your purchase
              </li>
            </ul>
            <p data-id="element-1829">
              Failure to pay fees may result in suspension or termination of
              your access to paid Services.
            </p>
          </div>
        </motion.div>

        {/* Disclaimers and Limitations */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1830">
          <div className="flex items-center gap-4 mb-6" data-id="element-1831">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1832">
              <AlertCircle size={24} data-id="element-1833" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1834">
              Disclaimers and Limitations of Liability
            </h2>
          </div>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1835">
            <p className="font-semibold" data-id="element-1836">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            </p>
            <p data-id="element-1837">
              SyNeural disclaims all warranties, including but not limited to
              warranties of merchantability, fitness for a particular purpose,
              non-infringement, and accuracy. We do not warrant that:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1838">
              <li data-id="element-1839">The Services will be uninterrupted, secure, or error-free</li>
              <li data-id="element-1840">
                Assessment results will be accurate, complete, or suitable for
                any particular purpose
              </li>
              <li data-id="element-1841">Any defects or errors will be corrected</li>
              <li data-id="element-1842">The Services will meet your specific requirements</li>
            </ul>
            <p className="font-semibold mt-6" data-id="element-1843">LIMITATION OF LIABILITY:</p>
            <p data-id="element-1844">
              To the maximum extent permitted by law, SyNeural shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, including but not limited to loss of profits,
              data, or opportunities, arising out of or related to your use of
              the Services, even if we have been advised of the possibility of
              such damages.
            </p>
            <p data-id="element-1845">
              Our total liability for any claims related to the Services shall
              not exceed the amount you paid us in the twelve months preceding
              the claim.
            </p>
          </div>
        </motion.div>

        {/* Indemnification */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1846">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1847">
            Indemnification
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1848">
            You agree to indemnify, defend, and hold harmless SyNeural, its
            affiliates, officers, directors, employees, and agents from any
            claims, liabilities, damages, losses, costs, or expenses (including
            reasonable attorneys' fees) arising out of or related to: (a) your
            use of the Services; (b) your violation of these Terms; (c) your
            violation of any third-party rights; or (d) your User Content.
          </p>
        </motion.div>

        {/* Termination */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1849">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1850">
            Termination
          </h2>

          <div className="space-y-4 text-ink-muted leading-relaxed" data-id="element-1851">
            <p data-id="element-1852">
              You may terminate your account at any time by contacting us or
              using account settings. We may suspend or terminate your access to
              the Services at any time, with or without cause or notice,
              including if:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4" data-id="element-1853">
              <li data-id="element-1854">You violate these Terms</li>
              <li data-id="element-1855">Your use poses a security or legal risk</li>
              <li data-id="element-1856">You fail to pay applicable fees</li>
              <li data-id="element-1857">We discontinue the Services</li>
            </ul>
            <p data-id="element-1858">
              Upon termination, your right to use the Services will immediately
              cease. Provisions that by their nature should survive termination
              (including intellectual property rights, disclaimers, and
              limitations of liability) will remain in effect.
            </p>
          </div>
        </motion.div>

        {/* Governing Law and Dispute Resolution */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1859">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1860">
            Governing Law and Dispute Resolution
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1861">
            These Terms shall be governed by and construed in accordance with
            the laws of [Jurisdiction], without regard to its conflict of law
            provisions. Any disputes arising out of or related to these Terms or
            the Services shall be resolved through binding arbitration in
            accordance with [Arbitration Rules], except that either party may
            seek injunctive relief in court. You waive any right to participate
            in class actions or class arbitrations.
          </p>
        </motion.div>

        {/* Changes to Terms */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1862">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1863">
            Changes to These Terms
          </h2>

          <p className="text-ink-muted leading-relaxed" data-id="element-1864">
            We reserve the right to modify these Terms at any time. We will
            notify you of material changes by posting the updated Terms on our
            website and updating the "Last Updated" date. Your continued use of
            the Services after such changes constitutes acceptance of the
            updated Terms. If you do not agree to the modified Terms, you must
            stop using the Services.
          </p>
        </motion.div>

        {/* Contact Information */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-surface-muted p-8 rounded-lg border border-line" data-id="element-1865">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1866">
            Contact Us
          </h2>
          <p className="text-ink-muted leading-relaxed mb-4" data-id="element-1867">
            If you have questions about these Terms of Service, please contact
            us:
          </p>
          <div className="space-y-2 text-ink-muted" data-id="element-1868">
            <p data-id="element-1869">
              <strong data-id="element-1870">Email:</strong>{' '}
              <a href="mailto:legal@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1871">
                legal@syneural.com
              </a>
            </p>
            <p data-id="element-1872">
              <strong data-id="element-1873">General Inquiries:</strong>{' '}
              <a href="mailto:contact@syneural.com" className="text-teal font-medium hover:underline" data-id="element-1874">
                contact@syneural.com
              </a>
            </p>
          </div>
        </motion.div>
      </section>
    </div>;
}